/* eslint-disable react/prop-types */
import { Box, Button, Heading, Link, Spinner, Text } from '@radix-ui/themes';
import axios from 'axios';
import ApiKey from 'Components/api/ApiKey';
import BaseApi from 'Components/api/BaseApi';
import { JobCategory } from 'jobs/types';
import Cookies from 'js-cookie';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateUtilsService } from 'shared/utils/date-utils.service';
// helpers
import { formatPhoneNumber, resultInfo } from '../../../Components/helpers';
import { SimpleCv, SimpleCvGetcontactResponse } from './types';

import './Questionnaire.scss';

const workTypeSettings = [
   { name: 1, text: 'searchWorkTypeFullTime' },
   { name: 2, text: 'searchWorkTypePartTime' },
   { name: 3, text: 'searchWorkTypeRemoteWork' },
   { name: 4, text: 'searchWorkTypeVariableSchedule' },
   { name: 5, text: 'searchWorkTypeFlexibleSchedule' },
   { name: 6, text: 'searchWorkTypeWatchMethod' },
];

export const Questionnaire = ({
   cvDetails,
   isOpenContact,
   refetch,
}: {
   refetch: () => void;
   isOpenContact: boolean;
   cvDetails: {
      jobInfo: any;
      jobApply: any;
      simpleCv: SimpleCv;
      categories: Array<JobCategory>;
      userdetail: any;
   };
}) => {
   const { t } = useTranslation('global');

   const userType = Cookies.get('user_type');
   const tokenKey = Cookies.get('tokenClient');
   const apikey = ApiKey;

   const { simpleCv, categories = [], userdetail = {} } = cvDetails;

   const { contactsData, contactsLoading, getContacts } = useSimpleCvContacts({
      token: tokenKey ?? '',
      apikey: apikey,
      cvId: simpleCv.id,
      isOpenContact,
      onSuccess: () => {
         refetch?.();
      },
   });

   const categoriesEl = [simpleCv?.category_id, simpleCv?.category_id_1, simpleCv?.category_id_2]
      .filter((cat) => !!cat)
      .map((catId) => {
         const category = categories.find((c) => c.id === catId);
         if (category) {
            return category.name;
         }
      })
      .join('; ');

   const specializations = [simpleCv.subcategory_id].reduce<Array<string>>((acc, subId) => {
      const subCat = simpleCv.subcategories.find((sub) => sub.id.toString() === subId?.toString());
      if (subCat) {
         return [...acc, subCat.name];
      }
      return acc;
   }, []);

   const renderEmail = () => {
      if (!contactsData?.email_address) {
         return null;
      }
      if (contactsData.email_address.includes('@email.grc.ua')) {
         return null;
      }
      return (
         <p>
            <i className="fa-solid fa-envelope" />
            <Link
               color="indigo"
               weight="medium"
               underline="hover"
               href={`mailto:${contactsData?.email_address}`}
            >
               {contactsData?.email_address}
            </Link>
         </p>
      );
   };

   function rederContent() {
      if (!isOpenContact) {
         return (
            <Box mb="4">
               <Button
                  color="red"
                  size="4"
                  radius="small"
                  loading={contactsLoading}
                  onClick={() => {
                     getContacts();
                  }}
               >
                  Відкрити контакти
               </Button>
            </Box>
         );
      }

      if (isOpenContact && contactsLoading) {
         return (
            <Box p="4" pb="6">
               <Spinner size="3" />
            </Box>
         );
      }

      if (isOpenContact && contactsData) {
         return (
            <Box mb="6" mt="2">
               <Box>
                  <Heading as="h2">
                     {contactsData?.last_name} {contactsData?.first_name}
                  </Heading>
               </Box>
               <Box>
                  {contactsData?.contact_number && (
                     <p>
                        <i className="fa-solid fa-phone" />
                        <Link
                           color="indigo"
                           underline="hover"
                           weight="medium"
                           href={`tel:${contactsData?.contact_number}`}
                        >
                           {formatPhoneNumber(contactsData?.contact_number)}
                        </Link>
                     </p>
                  )}
                  {renderEmail()}
               </Box>
            </Box>
         );
      }
   }

   return (
      <div className="cvDescription">
         <div className="personalInfoSection">
            <div>
               <h1>
                  {simpleCv.position}
                  <span className="ms-2 fs-4 text-muted">
                     ({t('employerInnerAccountdetail.questionnaire')})
                  </span>
               </h1>

               <ul className="candidate-info">
                  {userdetail?.birthday && (
                     <li>
                        <div className="dot"></div>
                        <Text weight="regular">
                           Вік - {DateUtilsService.toAge(userdetail.birthday)}
                        </Text>
                     </li>
                  )}

                  {userdetail?.cityInfo?.city_name && (
                     <li>
                        <div className="dot"></div>
                        <Text weight="regular">{userdetail?.cityInfo?.city_name}</Text>
                     </li>
                  )}

                  {simpleCv?.created && (
                     <li>
                        <div className="dot"></div>
                        <Text weight="regular">
                           Дата створення - {DateUtilsService.toAppFormat(simpleCv?.created)}
                        </Text>
                     </li>
                  )}
               </ul>

               {rederContent()}

               <div className="wishesInfoBox">
                  <p>
                     <i className="fa-solid fa-sitemap" />
                     <span>
                        {t('cvDescription.category')}
                        {': '}
                     </span>
                     {categoriesEl}
                  </p>
                  <p>
                     <i className="fa-solid fa-sitemap" />
                     <span>
                        {t('cvDescription.subCategory')}
                        {': '}
                     </span>
                     {specializations}
                  </p>
                  <p>
                     <i className="fa-sharp fa-solid fa-location-dot" />
                     <span>
                        {t('cvDescription.relocations')}
                        {': '}
                     </span>
                     {simpleCv?.city.city_name && simpleCv.city.city_name}
                     {simpleCv?.city.country_name && `, ${simpleCv.city.country_name}`}
                  </p>
                  <p>
                     <i className="fa-solid fa-clock" />
                     <span>
                        {t('cvDescription.workType')}
                        {': '}
                     </span>
                     {t(
                        `jobseekerListing.${workTypeSettings.find((i) => i.name.toString() === simpleCv.work_type.toString())?.text}`
                     )}
                  </p>
               </div>

               {simpleCv.info && (
                  <Box maxWidth="600px" mt="6" className="simple-cv__info-block">
                     <Heading as="h6" size="2" mb="0" weight="medium" color="gray">
                        Інформаційний блок: 
                     </Heading>
                     {simpleCv.info}
                  </Box>
               )}
            </div>

            <div className="profileImageBox">
               <img
                  alt="profile image"
                  className="profileImage"
                  src="/Images/jobseekerSide/dummy-profile.png"
               />
            </div>
         </div>
      </div>
   );
};

function useSimpleCvContacts({
   token,
   apikey,
   cvId,
   onError,
   onSuccess,
   isOpenContact,
}: {
   token: string;
   apikey: string;
   cvId: string | number;
   onError?: () => void;
   onSuccess?: () => void;
   isOpenContact: boolean;
}) {
   const [contactsData, setContactsData] = useState<SimpleCvGetcontactResponse['response'] | null>(
      null
   );
   const [smallLoading, setSmallLoading] = useState(false);
   const { t } = useTranslation('global');

   useEffect(() => {
      if (isOpenContact) {
         getContacts();
      }
   }, [isOpenContact]);

   const getContacts = async () => {
      try {
         setSmallLoading(true);

         const response = await axios.post<SimpleCvGetcontactResponse>(
            BaseApi + `/candidates/getSimplePageContacts/${cvId}`,
            null,
            {
               headers: {
                  key: apikey,
                  token: token,
                  'Content-Type': 'application/json',
               },
            }
         );

         setSmallLoading(false);

         if (response.data.status === 200) {
            setContactsData(response.data.response);
            onSuccess?.();
         } else if (response.data.status === 422) {
            resultInfo('warning', response.data.message, t('jobseekerManageAlert.close'));
         } else {
            resultInfo('error', response.data.message, t('jobseekerManageAlert.close'));
         }
      } catch (error: any) {
         setSmallLoading(false);

         if (error.response.data.status === 422) {
            resultInfo('warning', error.response.data.message, t('jobseekerManageAlert.close'));
         } else if (error.response.status === 500) {
            resultInfo('error', error.message, t('jobseekerManageAlert.close'));
         }

         console.error('Could not get contacts data', error);
      }
   };

   return { contactsData, contactsLoading: smallLoading, getContacts };
}
